import type { AppProps } from "next/app";

import dynamic from "next/dynamic";
import Head from "next/head";
import { appWithTranslation, useTranslation } from "next-i18next";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import "styles/index.css";

const AppProviders = dynamic(async () => await import("context"), { ssr: false });
const App = dynamic(async () => await import("components/App"), { ssr: false });

function FullPageErrorFallback({ error }: { error: { message: string } }) {
  const { t } = useTranslation();

  return (
    <div
      className="mx-auto mt-20 flex max-w-lg flex-col items-center justify-center text-red-600"
      role="alert"
    >
      <p>{t("errors.fatalError")}</p>
      <p>{error.message}</p>
    </div>
  );
}

function MyApp({ Component, pageProps }: AppProps) {
  useEffect(() => {
    posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "", {
      api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com",
      defaults: "2025-11-30",
      // Enable debug mode in development
      loaded: (posthog) => {
        if (process.env.NODE_ENV === "development") posthog.debug();
      },
    });
  }, []);

  return (
    <>
      <Head>
        <meta content="IE=edge" httpEquiv="X-UA-Compatible" />
        <meta
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
          name="viewport"
        />
        <meta content="yes" name="mobile-web-app-capable" />
        <meta content="yes" name="apple-mobile-web-app-capable" />
        <meta content="#ffffff" name="theme-color" />
        <title>Web Pos</title>
        <meta content="web pos" name="description" />
      </Head>
      <PostHogProvider client={posthog}>
        <ErrorBoundary
          FallbackComponent={FullPageErrorFallback}
          onError={(e, { componentStack }) =>
            posthog.captureException(e, { componentStack, stack: e.stack })
          }
        >
          <AppProviders>
            <App>
              <Component {...pageProps} />
            </App>
          </AppProviders>
        </ErrorBoundary>
      </PostHogProvider>
    </>
  );
}

export default appWithTranslation(MyApp);
